import { getUrlWithTransactionId } from '@shared/transactionId/getUrlWithTransactionId';
import { deconstructUri } from '@shared/helpers/deconstructUri';
import { Maker } from './buildMachine';
import { Ctx, Snap } from './core';
import { buildUrl } from './utils';

export const consentChanged: Maker<Ctx, Snap, null> = ({ makeEffectGroup }) => [
  makeEffectGroup(
    'fromGrantedToDenied',
    (_, { ctx, snap, pass, skip }) =>
      ctx.journal.utiq_consent_status === 'true' &&
      snap.cookies.utiq_consent_status === 'false'
        ? pass(null)
        : skip(''),

    ({ makeEffect }) => [
      makeEffect(
        'sendConsent',
        (_, { ctx, pass, skip, snap }) => {
          if (ctx.consentPayload === null) return skip('no consentPayload');
          if (
            Object.values(ctx.consentPayload)?.[0]?.consentState !== 'rejected'
          )
            return skip('consentPayload consentState outdated');

          const data = ctx.consentPayload;

          const configuredUrl =
            (window.Utiq?.config?.utiqHost ?? '') +
            (window.Utiq?.config?.apiPath ?? '');

          const url = buildUrl(ctx, ['utiqHost'])
            ?.tryPathFrom(configuredUrl)
            ?.withTransactionId()
            .render();

          if (!url) return skip('url unbuildable');

          const active = ctx.axios.active.find(
            ({ req }) =>
              req?.url === url &&
              JSON.stringify(req.data) === JSON.stringify(data),
          );

          if (active) return skip('already in flight', active, data);

          return pass({ url, data }, '');
        },
        ({ url, data }, { ctx }) => {
          ctx.consentPayload = null;
          ctx.axios.instance({
            url,
            data,
            method: 'POST',
            withCredentials: true,
            headers: { 'Content-Type': 'application/json' },
            timeout: 4000,
          });
        },
      ),
    ],
  ),

  makeEffectGroup(
    'fromDeniedtoGranted',
    (_, { ctx, snap, pass, skip }) =>
      ctx.journal.utiq_consent_status === 'false' &&
      snap.cookies.utiq_consent_status === 'true'
        ? pass(null)
        : skip(''),
    ({ makeEffect }) => [
      makeEffect(
        'consent_granted',
        (_, { snap, ctx, pass, skip }) => {
          // there is already a request in flight
          if (
            ctx.axios.active.some(
              ({ req }) =>
                req.method === 'get' && req.url?.includes('mno-precheck'),
            )
          )
            return skip('axios active');

          const { protocol, domain, path } = deconstructUri(
            `${ctx.config.utiqHost}/op/idconnect`,
          );

          if (!protocol || !domain || !path) {
            // we are missing the config required to make a request
            return skip(
              `Data missing from Utiq host - protocol: ${protocol}, domain: ${domain}, path: ${path}`,
            );
          }

          if (!snap.localStorage.utiqEligibility)
            return skip('!snap.localStorage.utiqEligibility');

          if (
            snap.localStorage.utiqEligibility.retryEligibilityCheckTime ===
              null ||
            new Date(
              snap.localStorage.utiqEligibility.retryEligibilityCheckTime,
            ) < snap.now
          ) {
            const type = 'consent_granted';

            const initialParam = snap.localStorage.utiqEligibility
              ? ''
              : '&initial=true';

            const url = getUrlWithTransactionId(
              `${protocol}://${domain}/${path}/mno-precheck?type=${type}${initialParam}`,
            );
            return pass(url);
          }

          return skip('end');
        },

        (url, { ctx }) => {
          ctx.axios.instance({
            method: 'get',
            url,
            timeout: 4000,
          });
        },
      ),
    ],
  ),
];
